import { useQueryClient } from "@tanstack/react-query";
import { SubmitHandler } from "react-hook-form";
import { toast } from "react-toastify";

import { useUpdateOrganizationPrimaryProvider } from "~/admins/hooks/organizations";
import { useListOrganizationProviders } from "~/admins/hooks/organizations/useListOrganizationProviders";
import { updateOrganizationPrimaryProviderFormSchema } from "~/admins/schemas/organization";
import { UpdateOrganizationPrimaryProviderForm } from "~/admins/types/organization";
import { PatientPharmacies } from "~/common/components/pharmacies";
import { SingleFieldFormCard } from "~/common/components/SingleFieldFormCard";
import { Note } from "~/common/components/ui";
import Select from "~/common/components/ui/select/Select";
import { PATIENT_STATUS } from "~/common/constants";
import { isPatientActive, Option } from "~/common/utils";
import { hasGeneralAccess } from "~/common/utils/checkUserPermissions";
import { hasAccountConfigured } from "~/patients/utils/hasAccountConfigured";
import {
  useGetCurrentTeamMember,
  useIsTeamMemberStaff,
} from "~/providers/hooks";
import { useUpdateNoteMutation } from "~/providers/hooks/queries/patientInformation/useUpdateNoteMutation";
import { getFullName } from "~/providers/utils";
import { PatientData } from "~/providers/utils/types";
import { CurrentCarePlan } from "./CurrentCarePlan";
import { UpdatePatientDetailsForm } from "./UpdatePatientDetailsForm";
import { UpdatePatientPhoneNumberForm } from "./UpdatePatientPhoneNumberForm";

interface PatientDetailsProps {
  patient: PatientData;
}

export const PatientDetails = ({ patient }: PatientDetailsProps) => {
  const mainPatientOrganization = patient.organization_patients[0];
  const queryClient = useQueryClient();
  const { data: teamMember } = useGetCurrentTeamMember();
  const { isStaff } = useIsTeamMemberStaff();
  const { data: providers } = useListOrganizationProviders(
    mainPatientOrganization.organization_id,
  );

  const primaryProvider = mainPatientOrganization.primary_provider;

  const activatedProviders = providers?.filter(
    (provider) => !provider.organization_team_members[0].deactivated_at,
  );

  const providersWithConfiguredAccount = activatedProviders?.filter(
    (provider) => hasAccountConfigured(provider),
  );

  const unassignedOptions: Option[] =
    mainPatientOrganization.status === PATIENT_STATUS.active
      ? []
      : [{ value: "", label: "Unassigned" }];

  const providerOptions: Option[] = (providersWithConfiguredAccount ?? []).map(
    (provider) => ({
      value: provider.id,
      label: `${provider.name} ${provider.last_name}`,
    }),
  );

  const isPrimaryProviderActive = Boolean(
    !primaryProvider?.organization_team_members[0].deactivated_at,
  );

  const providerHasGeneralAccess = hasGeneralAccess(
    teamMember?.user_permissions,
  );
  const { updateNoteMutation } = useUpdateNoteMutation(patient.id);
  const { mutateAsync: updatePrimaryProvider } =
    useUpdateOrganizationPrimaryProvider();

  const updateNote = async (note: string | null) => {
    await updateNoteMutation({ content: note });
  };

  const handleUpdatePrimaryProvider: SubmitHandler<
    UpdateOrganizationPrimaryProviderForm
  > = async ({ primaryProvider }) => {
    primaryProvider ??= null;
    try {
      await updatePrimaryProvider({
        organizationId: mainPatientOrganization.organization_id,
        patientId: patient.id,
        primaryProviderId: primaryProvider,
      });
      await queryClient.invalidateQueries({
        queryKey: ["getPatient", patient.id],
      });
      toast.success("Primary provider updated successfully");
    } catch (e) {
      toast.error("There was an error updating the primary provider");
      throw e;
    }
  };

  return (
    <div className="flex w-full flex-col gap-4 p-6 sm:px-14 sm:py-8">
      <Note content={patient.note?.content} handleSubmitNote={updateNote} />

      <SingleFieldFormCard
        title="Primary Provider"
        submitButtonLabel="Save"
        defaultValues={{
          primaryProvider: isPrimaryProviderActive
            ? (mainPatientOrganization.primary_provider_id ?? null)
            : null,
        }}
        validationSchema={updateOrganizationPrimaryProviderFormSchema}
        onSubmit={handleUpdatePrimaryProvider}
        renderField={({ isEditing, control, formState: { errors } }) =>
          isEditing ? (
            <Select
              id="primaryProvider"
              placeholder="Unassigned"
              options={[...unassignedOptions, ...providerOptions]}
              control={control}
              containerClassName="w-1/2"
              error={errors.primaryProvider?.message}
            />
          ) : primaryProvider ? (
            <p className="text-sm text-gray-700">
              {getFullName(primaryProvider?.name, primaryProvider?.last_name)}
            </p>
          ) : (
            <p className="whitespace-pre-wrap italic leading-5 tracking-wide text-gray-60">
              Unassigned
            </p>
          )
        }
      />

      {providerHasGeneralAccess &&
        isPatientActive(mainPatientOrganization.status) && <CurrentCarePlan />}

      <UpdatePatientPhoneNumberForm patient={patient} />
      <UpdatePatientDetailsForm patient={patient} />

      {providerHasGeneralAccess && (
        <PatientPharmacies
          patientId={patient.id}
          className="md:max-w-none"
          title="Pharmacies"
          canEdit={!isStaff}
        />
      )}
    </div>
  );
};
